<template>
    <asli-form-section title="Make your payment" heading-level="h2">
        <div class="mb-4">
            <paragraph>Please enter your card details below to make payment. </paragraph>
            <paragraph>
                For your security, your card provider may also need you to verify your card using your mobile banking
                app or a passcode.
            </paragraph>
            <paragraph>
                If you are having difficulty, you can call us on 0818 22 22 22 to complete the payment.
            </paragraph>
        </div>
        <div class="border-2 border-section-header p-6 rounded-[3px]">
            <div class="flex flex-col sm:flex-row gap-6">
                <div class="order-1 sm:order-2 w-full sm:w-1/2">
                    <div v-if="isPayMonthly">
                        <h3 class="text-lg font-bold mb-5">Payment Details</h3>
                        <div class="text-base mb-[15px]">
                            <p class="pb-2">Deposit: €{{ quoteMonthlyDeposit }}</p>
                            <p class="mb-1">{{ monthlyInstallments }} monthly payments: €{{ quoteMonthlyPayment }}</p>
                            <p class="mb-4">Final monthly payment: €{{ monthlyInitialPayment }}</p>
                            <div class="font-bold flex flex-col">
                                <span class="text-sm">Total payable (inc. interest & fees): </span>
                                <span class="text-lg">€{{ quoteMonthlyTotalPayable }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="border-b-2 border-section-header">
                        <h3 class="text-lg font-bold mb-5">Total Payable Today</h3>
                        <h4 class="text-2xl font-bold mb-5">€{{ appendDecimal(selectedQuote.premium) }}</h4>
                    </div>
                    <div class="mt-5">
                        <h3 class="text-lg font-bold mb-5">Quote Details</h3>
                        <div v-for="prop in properties" :key="prop.label" class="text-base mb-[15px]">
                            <p class="font-bold">{{ prop.label }}:</p>
                            <p>{{ prop.value }}</p>
                        </div>
                    </div>
                </div>
                <div
                    class="border-2 border-section-header rounded-[3px] w-full transform-container overflow-hidden basis-1/2 order-2 sm:order-1"
                >
                    <div class="transform-wrapper">
                        <iframe
                            id="payment-frame"
                            class="w-full h-[610px]"
                            title="Secure payment form"
                            :src="iframeUrl ?? ''"
                        />
                    </div>
                </div>
            </div>
        </div>
    </asli-form-section>
    <button-bar class="flex-col items-center md:items-end">
        <asli-button id="payment-back" color="secondary" type="button" @click="goBack">Back</asli-button>
    </button-bar>
</template>

<script lang="ts" setup>
import { ROUTE_NAMES } from "@/router/types";
import { useMainStore } from "@/stores/main";
import { AsliFormSection, AsliButton, appendDecimal, isToday } from "@mbs-npm/anpost-ui";
import { useRouter } from "vue-router";
import ButtonBar from "@/components/ButtonBar.vue";
import { useProposalStore } from "@/stores/risk/risk";
import { computed, onBeforeMount } from "vue";
import Paragraph from "@/components/Paragraph.vue";
import { formatDate, formatDateAndTime } from "@/utils/index";
import { goToError } from "@/utils/error-handling";
import { APP_STEP } from "@/stores/types";

const mainStore = useMainStore();
const proposalStore = useProposalStore();
mainStore.setAppStep(APP_STEP.PAYMENT);

const router = useRouter();
const selectedQuote = computed(() => proposalStore.proposal.quote);
const isPayMonthly = computed(() => proposalStore.proposal.is_pay_monthly);
const quoteMonthlyDeposit = computed(() => appendDecimal(selectedQuote?.value?.monthly_deposit?.toString(), true));
const quoteMonthlyPayment = computed(() => appendDecimal(selectedQuote?.value?.monthly_payment?.toString(), true));
const quoteMonthlyTotalPayable = computed(() =>
    appendDecimal(selectedQuote?.value?.monthly_total_payable?.toString(), true)
);
const monthlyInitialPayment = computed(() =>
    appendDecimal(selectedQuote?.value?.monthly_initial_payment?.toString(), true)
);
const monthlyInstallments = computed(() => {
    if (proposalStore?.proposal?.quote?.monthly_installments) {
        return proposalStore?.proposal?.quote?.monthly_installments - 1;
    }
});

const iframeUrl = computed(() => proposalStore.payment.url);

function goBack() {
    const routeName = isPayMonthly.value ? ROUTE_NAMES.DirectDebit : ROUTE_NAMES.Marketing;
    router.push({
        name: routeName,
        query: {
            uuid: proposalStore.proposal.uuid
        }
    });
}

onBeforeMount(async () => {
    try {
        if (mainStore.fromPaymentFailed) {
            mainStore.setFromPaymentFailed(false);
        }
        if (proposalStore.proposal.is_purchased) {
            router.push({
                name: ROUTE_NAMES.ThankYou,
                query: {
                    uuid: proposalStore.proposal.uuid
                }
            });
            return;
        }

        mainStore.setLoadingStatePopupText("Preparing payment...");
        mainStore.setLoadingState(true);
        await proposalStore.getPaymentPageURL();
    } catch {
        goToError(proposalStore.proposal.uuid as string);
    } finally {
        mainStore.setLoadingState(false);
    }
});

const properties = computed(() =>
    selectedQuote.value
        ? [
              {
                  label: "Insurer Details",
                  value: selectedQuote.value.insurer_name
              },
              {
                  label: "Cover Effective From",
                  value: isToday(selectedQuote.value.cover_effective_from)
                      ? formatDate(selectedQuote.value.cover_effective_from)
                      : formatDateAndTime(selectedQuote.value.cover_effective_from)
              },
              {
                  label: "Cover Effective To",
                  value: formatDateAndTime(selectedQuote.value.cover_effective_to)
              }
          ]
        : []
);
</script>

<style scoped>
@media (max-width: 511px) {
    .transform-container {
        height: 337.5px; /* 75% of 450px */
    }

    .transform-wrapper {
        transform: scale(0.75);
        transform-origin: top left;
        width: 133%;
        height: 450px;
    }
}

@media (min-width: 512px) {
    .transform-container {
        height: 610px;
    }

    .transform-wrapper {
        width: 100%;
        height: 610px;
    }
}

@media (min-width: 640px) and (max-width: 827px) {
    .transform-container {
        height: 457.5px; /* 75% of 610px */
    }

    .transform-wrapper {
        transform: scale(0.75);
        transform-origin: top left;
        width: 133%;
        height: 610px;
    }
}

@media (min-width: 828px) {
    .transform-container {
        height: 610px;
    }

    .transform-wrapper {
        width: 100%;
        height: 610px;
    }
}
</style>
